/**
 * Stream B — B45: 15-Minute Client Activation Wizard
 *
 * Agency creates new client: tenant provisioned, basic config,
 * sample data loaded, client can login. Target: 15 minutes.
 * Real apiFetch. No mocks. CSS variables only.
 */
import { useState, type FormEvent } from "react";
import { apiFetch } from "@/lib/api-client";
import { useNavigate } from "react-router";
import { Zap, Building2, Palette, Database, CheckCircle2, Loader2, ArrowRight, ArrowLeft } from "lucide-react";

type Step = "basics" | "industry" | "branding" | "data" | "done";

const INDUSTRIES = [
  "Dental", "Medical / Healthcare", "Legal", "Real Estate",
  "Home Services", "Fitness / Wellness", "Salon / Spa", "Restaurant",
  "Education", "Financial Services", "E-commerce", "Other",
] as const;

interface ClientConfig {
  businessName: string;
  contactEmail: string;
  industry: string;
  timezone: string;
  primaryColor: string;
  loadSampleData: boolean;
}

const STEPS: { key: Step; label: string; icon: typeof Building2 }[] = [
  { key: "basics", label: "Business Info", icon: Building2 },
  { key: "industry", label: "Industry", icon: Zap },
  { key: "branding", label: "Branding", icon: Palette },
  { key: "data", label: "Sample Data", icon: Database },
  { key: "done", label: "Complete", icon: CheckCircle2 },
];

export default function ActivateClientPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("basics");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdTenantId, setCreatedTenantId] = useState<string | null>(null);

  const [config, setConfig] = useState<ClientConfig>({
    businessName: "",
    contactEmail: "",
    industry: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    primaryColor: "#1040CC",
    loadSampleData: true,
  });

  const currentIdx = STEPS.findIndex((s) => s.key === step);

  const handleCreate = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: { tenantId: string; loginUrl: string } }>("/agency/clients/activate", {
        method: "POST",
        body: JSON.stringify(config),
      });
      setCreatedTenantId(res.data.tenantId);
      setStep("done");
    } catch {
      setError("Failed to create client account. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const goNext = () => {
    if (step === "basics") setStep("industry");
    else if (step === "industry") setStep("branding");
    else if (step === "branding") setStep("data");
    else if (step === "data") handleCreate();
  };

  const goBack = () => {
    if (step === "industry") setStep("basics");
    else if (step === "branding") setStep("industry");
    else if (step === "data") setStep("branding");
  };

  const isNextDisabled =
    (step === "basics" && (!config.businessName.trim() || !config.contactEmail.trim())) ||
    (step === "industry" && !config.industry);

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Zap className="h-5 w-5 text-[var(--color-primary)]" />
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Activate New Client</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">Set up a new client account in under 15 minutes</p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}

      {/* Progress */}
      <div className="flex gap-1">
        {STEPS.map((s, i) => (
          <div key={s.key} className={`h-1.5 flex-1 rounded-full transition-all ${i <= currentIdx ? "bg-[var(--color-primary)]" : "bg-[var(--color-border)]"}`} />
        ))}
      </div>

      {/* Step: Basics */}
      {step === "basics" && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 space-y-4">
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Business Information</h2>
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Business Name</label>
            <input
              value={config.businessName}
              onChange={(e) => setConfig((c) => ({ ...c, businessName: e.target.value }))}
              placeholder="e.g. Smith Dental Practice"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Contact Email</label>
            <input
              type="email"
              value={config.contactEmail}
              onChange={(e) => setConfig((c) => ({ ...c, contactEmail: e.target.value }))}
              placeholder="owner@business.com"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Timezone</label>
            <select
              value={config.timezone}
              onChange={(e) => setConfig((c) => ({ ...c, timezone: e.target.value }))}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            >
              {(() => { try { return Intl.supportedValuesOf("timeZone"); } catch { return ["UTC", "America/New_York", "Europe/London", "Europe/Berlin", "Asia/Tokyo"]; } })()
                .map((tz) => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Step: Industry */}
      {step === "industry" && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 space-y-4">
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Industry</h2>
          <p className="text-sm text-[var(--color-muted-foreground)]">This pre-configures pipeline stages, workflow templates, and default services.</p>
          <div className="grid grid-cols-2 gap-2">
            {INDUSTRIES.map((ind) => (
              <button
                type="button"
                key={ind}
                onClick={() => setConfig((c) => ({ ...c, industry: ind }))}
                className={`rounded-lg border p-3 text-sm text-left transition-colors ${
                  config.industry === ind
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5 text-[var(--color-primary)] font-medium"
                    : "border-[var(--color-border)] text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                }`}
              >
                {ind}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step: Branding */}
      {step === "branding" && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 space-y-4">
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Branding</h2>
          <p className="text-sm text-[var(--color-muted-foreground)]">Set the primary brand color. You can customize more later.</p>
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-1">Primary Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={config.primaryColor}
                onChange={(e) => setConfig((c) => ({ ...c, primaryColor: e.target.value }))}
                className="h-10 w-10 rounded border border-[var(--color-border)] cursor-pointer"
              />
              <input
                value={config.primaryColor}
                onChange={(e) => setConfig((c) => ({ ...c, primaryColor: e.target.value }))}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] w-28 font-mono"
              />
            </div>
          </div>
          <div className="rounded-lg p-4" style={{ backgroundColor: config.primaryColor + "10", borderLeft: `3px solid ${config.primaryColor}` }}>
            <p className="text-sm" style={{ color: config.primaryColor }}>Preview: {config.businessName || "Your Business"}</p>
          </div>
        </div>
      )}

      {/* Step: Sample Data */}
      {step === "data" && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 space-y-4">
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Sample Data</h2>
          <label className="flex items-center justify-between rounded-lg border border-[var(--color-border)] p-4 cursor-pointer">
            <div>
              <p className="text-sm font-medium text-[var(--color-foreground)]">Load sample data</p>
              <p className="text-xs text-[var(--color-muted-foreground)]">Pre-populate with example contacts, deals, and workflows so the client sees a working dashboard immediately.</p>
            </div>
            <input
              type="checkbox"
              checked={config.loadSampleData}
              onChange={(e) => setConfig((c) => ({ ...c, loadSampleData: e.target.checked }))}
              className="rounded"
            />
          </label>

          <div className="rounded-lg bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
            <p className="font-medium text-[var(--color-foreground)] mb-2">What happens next:</p>
            <ul className="space-y-1 text-xs">
              <li>1. Tenant database provisioned on Hetzner</li>
              <li>2. Schema migrated (270+ tables)</li>
              <li>3. Industry-specific pipeline + workflows created</li>
              <li>4. {config.loadSampleData ? "Sample contacts, deals, campaigns loaded" : "Empty workspace ready"}</li>
              <li>5. Login link sent to {config.contactEmail || "client email"}</li>
            </ul>
          </div>
        </div>
      )}

      {/* Step: Done */}
      {step === "done" && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-8 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-[var(--color-success)] mb-4" />
          <h2 className="text-lg font-semibold text-[var(--color-foreground)] mb-2">Client Activated</h2>
          <p className="text-sm text-[var(--color-muted-foreground)] mb-4">
            {config.businessName} is ready. Login instructions sent to {config.contactEmail}.
          </p>
          <div className="flex justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/agency")}
              className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
            >
              Back to Dashboard
            </button>
            {createdTenantId && (
              <button
                type="button"
                onClick={() => navigate(`/agency/manage/${createdTenantId}`)}
                className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm text-white hover:opacity-90"
              >
                Manage Client
              </button>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      {step !== "done" && (
        <div className="flex justify-between">
          {currentIdx > 0 ? (
            <button type="button" onClick={goBack} className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          ) : <div />}
          <button
            type="button"
            onClick={goNext}
            disabled={isNextDisabled || isCreating}
            className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-primary)] px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {isCreating ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Creating...</>
            ) : step === "data" ? (
              <><Zap className="h-4 w-4" /> Activate Client</>
            ) : (
              <>Next <ArrowRight className="h-4 w-4" /></>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
